import { formatTime } from '@/scripts/tools/utils';
import COS from '@/plugins/cos_sdk/src/cos';
import dao from '@/scripts/helper/dao';
import keyBox from '@/scripts/helper/keyBox';
import { store } from '@/scripts/store';
import cos from './index';

// 获取存储桶的真实acl
function getBucketPublicAcl(params) {
  return new Promise(resolve => {
    cos.getBucketAcl(
      {
        Bucket: params.Bucket,
        Region: params.Region,
      },
      (err1, aclData) => {
        let ACL;
        if (err1 && err1.statusCode === 403) ACL = 'AccessDenied';
        if (aclData) {
          ACL = aclData.ACL || (aclData.headers && aclData.headers['x-cos-acl']) || '';
        }
        if (ACL) {
          resolve(ACL);
        } else {
          cos.headBucket(
            {
              Bucket: params.Bucket,
              Region: params.Region,
            },
            (err2, headData) => {
              ACL = (headData && headData.headers && headData.headers['x-cos-acl']) || 'Unknown';
              resolve(ACL);
            },
          );
        }
      },
    );
  });
}

// 获取存储桶的多版本情况
function getBucketVersioning(params) {
  return new Promise(resolve => {
    cos.getBucketVersioning(
      {
        Bucket: params.Bucket,
        Region: params.Region,
      },
      (vErr, vData) => {
        let versioning = '';
        if (vErr) {
          versioning = vErr.statusCode === 403 ? 'AccessDenied' : 'Unknown';
        } else {
          const vConf = (vData && vData.VersioningConfiguration) || {};
          versioning = vConf.Status || 'Disabled';
        }
        resolve(versioning);
      },
    );
  });
}

// 获取存储桶的极智压缩配置
function getBucketImageSlim({ Bucket, Region }, cb) {
  return new Promise(resolve => {
    const protocol = cos.options.Protocol || 'https:';
    const url = `${protocol}//${Bucket}.pic.${Region}.myqcloud.com/`;
    cos.request({
      Method: 'GET',
      Bucket,
      Region,
      Url: url,
      Query: {
        'image-slim': '',
      },
    }, (err, data) => {
      let imageSlim = {};
      if (err) imageSlim = err.code;
      else imageSlim = data.ImageSlim;
      if (imageSlim?.Suffixs?.Suffix && !Array.isArray(imageSlim.Suffixs.Suffix)) {
        imageSlim.Suffixs.Suffix = [imageSlim.Suffixs.Suffix];
      }
      cb && cb(imageSlim);
      resolve(imageSlim);
    });
  });
}

// 获取存储桶的全球加速情况
function getBucketAccelerate(params, callback) {
  return new Promise(resolve => {
    cos.getBucketAccelerate(
      {
        Bucket: params.Bucket,
        Region: params.Region,
      },
      (vErr, vData) => {
        let acceleration = '';
        if (vErr) {
          acceleration = vErr.statusCode === 403 ? 'AccessDenied' : 'Unknown';
        } else {
          const vConf = (vData && vData.AccelerateConfiguration) || {};
          acceleration = vConf.Status || 'Disabled';
        }
        callback && callback(null, acceleration);
        resolve(acceleration);
      },
    );
  });
}

// 获取存储桶的 MAZ 情况
function getBucketMazConfig(params) {
  const { Bucket, Region } = params;
  const opt = { Bucket, Region };
  return new Promise(resolve => {
    cos.headBucket({ Bucket, Region }, (err, data) => {
      let MAZConfig = 'Off';
      if (data) {
        const { headers } = data;
        MAZConfig = headers['x-cos-bucket-az-type'] || 'Off';
        resolve(MAZConfig);
      } else {
        MAZConfig = err.statusCode === 403 ? 'AccessDenied' : 'Unknown';
        opt.MaxKeys = 1;
        cos.getBucket(opt, (_err, _data) => {
          if (_data) {
            const item = _data.Contents[0];
            if (item && item.StorageClass && item.StorageClass.includes('MAZ')) {
              MAZConfig = 'MAZ';
            }
          }
          resolve(MAZConfig);
        });
      }
    });
  });
}

// 获取完整的存储桶详情
async function getBucketInfo(params) {
  const { Bucket, Region } = params;
  const [acl, versioning, imageSlim, MAZConfig] = await Promise.all([
    getBucketPublicAcl(params),
    getBucketVersioning(params),
    getBucketImageSlim(params),
    getBucketMazConfig(params),
  ]);
  const bucketInfo = {
    Bucket,
    Region,
    imageSlim,
    MAZConfig,
    CreateTime: params.CreateTime || '-',
    acl,
    versioning,
  };
  return bucketInfo;
}

// 获取对象的真实acl
function getObjectRealAcl(params) {
  return new Promise(resolve => {
    const { BucketAcl } = params;
    cos.getObjectAcl(params, (err, data) => {
      const acl = data && data.ACL;
      if (!acl || ['default', 'tce-default'].includes(acl)) {
        if (BucketAcl) {
          resolve(BucketAcl);
        } else {
          getBucketPublicAcl(params).then(resolve);
        }
      } else {
        resolve(acl);
      }
    });
  });
}

// 判断存储桶是否开启跨区域复制
function checkBucketReplication(params) {
  const { Bucket, Region, Prefix } = params;
  return new Promise(resolve => {
    cos.getBucketReplication(
      {
        Bucket,
        Region,
      },
      (err, data) => {
        let rules = [];
        try {
          rules = data.ReplicationConfiguration.Rules || [];
        } catch (e) {
          //
        }
        if (!(rules instanceof Array)) {
          rules = [rules];
        }
        const enabledRules = rules.filter(v => v.Status === 'Enabled');
        let enableReplication = false;
        if (!Prefix && enabledRules.length > 0) {
          enableReplication = true;
        } else {
          enabledRules.forEach(rule => {
            if (rule.Prefix === Prefix) {
              enableReplication = true;
            }
          });
        }
        resolve(enableReplication);
      },
    );
  });
}

// 启用或者暂停存储桶的全球加速功能
function putBucketAccelerate(Bucket, Region, acceleration, editAcceleration) {
  return new Promise((resolve, reject) => {
    const newStatus = editAcceleration ? 'Enabled' : 'Suspended';
    // 从未开启全球加速并且编辑状态是开启状态 || 开启了全球加速并且编辑状态与之前的状态不一致
    if ((!acceleration && editAcceleration) || (acceleration && newStatus !== acceleration)) {
      cos.putBucketAccelerate(
        {
          Bucket,
          Region,
          AccelerateConfiguration: {
            Status: newStatus,
          },
        },
        (err, data) => {
          err && reject(err);
          data && resolve(data);
        },
      );
    } else {
      resolve();
    }
  });
}

// 启用或者暂停存储桶的版本控制功能
function putBucketVersioning(Bucket, Region, versioning, editVersioning) {
  return new Promise((resolve, reject) => {
    const newStatus = editVersioning ? 'Enabled' : 'Suspended';
    // 从未开启多版本并且编辑状态是开启状态 || 开启了多版本并且编辑状态与之前的状态不一致
    if ((!versioning && editVersioning) || (versioning && newStatus !== versioning)) {
      cos.putBucketVersioning(
        {
          Bucket,
          Region,
          VersioningConfiguration: {
            Status: newStatus,
          },
        },
        (err, data) => {
          err && reject(err);
          data && resolve(data);
        },
      );
    } else {
      resolve();
    }
  });
}

// 修改极智压缩配置
function setBucketImageSlim(Bucket, Region, imageSlim, editImageSlim) {
  return new Promise((resolve, reject) => {
    const isSame = typeof imageSlim === 'object' && imageSlim.SlimMode === editImageSlim
        && imageSlim.Suffix?.Suffixs?.join(',') === editImageSlim.Suffix;
    // 从未开启多版本并且编辑状态是开启状态 || 开启了多版本并且编辑状态与之前的状态不一致
    if (!isSame) {
      const protocol = cos.options.Protocol || 'https:';
      const url = `${protocol}//${Bucket}.pic.${Region}.myqcloud.com/`;
      const cb = (err, data) => {
        err && reject(err);
        data && resolve(data);
      };
      if (editImageSlim.SlimMode) {
        cos.request({
          Method: 'PUT',
          Url: url,
          Query: {
            'image-slim': '',
          },
          Headers: {
            'Content-Type': 'application/xml',
          },
          Body: COS.util.json2xml({ ImageSlim: editImageSlim }),
        }, cb);
      } else {
        cos.request({
          Method: 'DELETE',
          Url: url,
          Query: {
            'image-slim': '',
          },
        }, cb);
      }
    } else {
      resolve();
    }
  });
}
/**
 * 获取文件对象归档恢复状态
 * @param {Object} params
 */
function getRestoreState({ Bucket, Region, Key }) {
  return new Promise((resolve, reject) => {
    cos.headObject({ Bucket, Region, Key }, (err, data) => {
      if (err) {
        if (err.statusCode === 404) {
          reject(err);
        } else if (err.error === 'Forbidden' && err.statusCode === 403) {
          err.code = 'no_permissions_to_head_object';
          reject(err);
        } else {
          reject(err);
        }
      } else {
        const { headers } = data;
        const storageClass = headers['x-cos-storage-class'] || '';
        const restoreStatus = headers['x-cos-restore'] || '';
        if (storageClass.includes('ARCHIVE') && (!restoreStatus || restoreStatus === 'ongoing-request="true"')) {
          // 归档文件未恢复
          resolve(false);
        } else {
          resolve(true);
        }
      }
    });
  });
}
/**
 * 获取文件对象归档恢复状态
 * @param {Object} params
 */
function getRestoreInfo(params) {
  return new Promise((resolve, reject) => {
    cos.headObject(params, (err, data) => {
      if (err) {
        reject(err);
        return;
      }
      const { headers } = data;
      const restoreStatus = headers['x-cos-restore'] || '';
      const obj = {};
      if (!restoreStatus) {
        obj.status = 'unrestore';
      } else if (restoreStatus === 'ongoing-request="true"') {
        obj.status = 'restoring';
      } else if (restoreStatus.indexOf('ongoing') === 0) {
        const matchArr = restoreStatus.match(/ongoing-request="(.*)", expiry-date="(.*)"/);
        obj.status = 'restored';
        obj.deleteTime = formatTime(new Date(matchArr[2]));
      }

      resolve(obj);
    });
  });
}
// 获取cookie
function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
}

function fetchWhiteList() {
  const keyType = keyBox.getKeyType();
  // 只有当有cookie且keyType为QcloudAccount时才认定为真正的登录状态
  const isLogined = !!(getCookie('skey') || getCookie('p_skey')) && keyType === 'QcloudAccount';

  if (!isLogined) {
    return Promise.resolve(false);
  }
  // 获取ownerUin
  const uin = store.userInfo?.OwnerUin;
  return new Promise(resolve => {
    dao.getWhiteList({
      uin,
      keyList: ['COS_CONSOLE_RESPONSE_CONTROL'],
    })
      .then(res => {
        // 检查返回结果中是否包含 COS_CONSOLE_RESPONSE_CONTROL
        if (res && res.data) {
          const whitelistData = res.data;
          // 直接获取 COS_CONSOLE_RESPONSE_CONTROL 数组
          const consoleResponseControlList = whitelistData.COS_CONSOLE_RESPONSE_CONTROL;

          if (Array.isArray(consoleResponseControlList)) {
            // 检查当前 ownerUin 是否在白名单数组中
            const isInnerWhiteList = consoleResponseControlList.includes(uin);
            resolve(isInnerWhiteList);
          } else {
            resolve(false);
          }
        } else {
          resolve(false);
        }
      })
      .catch(error => {
        console.error('fetchWhiteList 调用失败:', error);
        resolve(false);
      });
  });
}
/**
 * 获取文件对象的下载链接
 * @param {Object} params
 */
async function getObjectDownloadUrl(params) {
  const isInWhitelist = await fetchWhiteList();
  console.log('用户是否在白名单中:', isInWhitelist);
  // 根据白名单状态决定查询参数
  const queryParams = {
    'response-content-disposition': 'attachment',
  };

  // 如果不在白名单中，添加强制下载的 content-type
  if (!isInWhitelist) {
    queryParams['response-content-type'] = 'application/octet-stream';
  }

  return new Promise((resolve, reject) => {
    cos.getObjectUrl(
      {
        ...params,
        Sign: true,
        Expires: 3600,
        Query: queryParams,
      },
      (err, data) => {
        if (err) {
          reject(err);
          return;
        }
        const { Url } = data;
        resolve(Url);
      },
    );
  });
}

const cosExpand = {
  // 存储桶相关
  getBucketPublicAcl, // 获取公共权限
  getBucketInfo,
  getBucketVersioning,
  getBucketImageSlim,
  getBucketMazConfig,
  getBucketAccelerate,
  checkBucketReplication,
  putBucketAccelerate,
  putBucketVersioning,
  setBucketImageSlim,

  // 文件对象相关
  getObjectRealAcl,
  getRestoreInfo,
  getRestoreState,
  getObjectDownloadUrl,
  // 白名单相关
  fetchWhiteList,
};
export default cosExpand;
